// components/IdentityBadge.jsx: compact identity chip + edit modal.
//
// Visible in the top-right while the user is in a room (or playing a
// game). Click opens a modal wrapping the full IdentityPanel so the
// user can rename / recolor without leaving the room.

import { useEffect, useState, useCallback } from 'preact/hooks';
import { getOrCreateIdentity } from '../identity/identity.js';
import { AvatarCircle } from './AvatarCircle.jsx';
import { IdentityPanel } from './IdentityPanel.jsx';
import { identityBadgeLabel } from './avatar-helpers.js';
import '../styles/identity-badge.css';

/**
 * @param {{ closeGlyph?: string }} props  closeGlyph: the editor's close
 *   mark ("×"; the cream room header passes "X", which its pixel font draws
 *   clearly).
 */
export function IdentityBadge({ closeGlyph = '×' }) {
  const [identity, setIdentity] = useState(/** @type {any} */ (null));
  const [open, setOpen] = useState(false);

  useEffect(() => {
    setIdentity(getOrCreateIdentity());
  }, []);

  const refresh = useCallback((next) => {
    setIdentity(next || getOrCreateIdentity());
  }, []);

  const handleClose = useCallback(() => setOpen(false), []);
  const handleOpen = useCallback(() => setOpen(true), []);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  if (!identity) return null;

  return (
    <>
      <button
        type="button"
        class="identity-badge"
        data-testid="identity-badge"
        onClick={handleOpen}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={identityBadgeLabel(identity.display_name)}
        title="Edit identity"
      >
        <AvatarCircle color={identity.avatar_color} name={identity.display_name} size={32} />
        <span class="identity-badge-name" data-testid="identity-badge-name" title={identity.display_name}>
          {identity.display_name}
        </span>
      </button>
      {open && (
        <div
          class="identity-modal-backdrop"
          data-testid="identity-modal-backdrop"
          onClick={handleClose}
        >
          <div
            class="identity-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="identity-modal-title"
            data-testid="identity-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div class="identity-modal-header">
              <h2 id="identity-modal-title" class="identity-modal-title">Edit identity</h2>
              <button
                type="button"
                class="identity-modal-close"
                data-testid="identity-modal-close"
                aria-label="Close"
                onClick={handleClose}
              >
                {closeGlyph}
              </button>
            </div>
            <IdentityPanel onChange={refresh} />
            <div class="identity-modal-footer">
              <button
                type="button"
                class="identity-modal-done"
                data-testid="identity-modal-done"
                onClick={handleClose}
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
